import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/safeClient';
import { RealtimeChannel } from '@supabase/supabase-js';
import { format } from 'date-fns';

export function useNotifications() {
  const { user } = useAuth();
  const checkIntervalRef = useRef<NodeJS.Timeout>();
  const channelRef = useRef<RealtimeChannel>();

  useEffect(() => {
    if (!user) return;

    // Request notification permission
    requestNotificationPermission();

    // Check for upcoming meetups every minute
    checkUpcomingMeetups();
    checkIntervalRef.current = setInterval(checkUpcomingMeetups, 60000); // Every 1 minute

    // Subscribe to deal updates
    subscribeToDeals();

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [user]);

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return;
    }

    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      console.log('Notification permission:', permission);
    }
  };

  const checkUpcomingMeetups = async () => {
    if (!user || Notification.permission !== 'granted') return;

    try {
      const now = new Date();
      const fifteenMinutesFromNow = new Date(now.getTime() + 15 * 60000);
      const sixteenMinutesFromNow = new Date(now.getTime() + 16 * 60000);

      const { data: deals, error } = await supabase
        .from('deals')
        .select(`
          *,
          listing:listings(title),
          buyer:profiles!deals_buyer_id_fkey(name),
          seller:profiles!deals_seller_id_fkey(name)
        `)
        .eq('status', 'active')
        .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`)
        .gte('meetup_time', fifteenMinutesFromNow.toISOString())
        .lte('meetup_time', sixteenMinutesFromNow.toISOString());

      if (error) throw error;

      deals?.forEach((deal) => {
        const otherParty = deal.buyer_id === user.id ? deal.seller : deal.buyer;
        
        showNotification(
          'Meetup Reminder',
          `Your meetup with ${otherParty.name} for "${deal.listing.title}" is in 15 minutes at ${deal.meetup_location_address}`,
          '/near-me'
        );
      });
    } catch (error) {
      console.error('Error checking upcoming meetups:', error);
    }
  };

  const subscribeToDeals = () => {
    if (!user || Notification.permission !== 'granted') return;

    channelRef.current = supabase
      .channel('deal-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'deals',
          filter: `buyer_id=eq.${user.id},seller_id=eq.${user.id}`,
        },
        async (payload) => {
          const updatedDeal = payload.new;
          const oldDeal = payload.old;

          // Fetch related data
          const { data: dealData } = await supabase
            .from('deals')
            .select(`
              listing:listings(title),
              buyer:profiles!deals_buyer_id_fkey(name),
              seller:profiles!deals_seller_id_fkey(name)
            `)
            .eq('id', updatedDeal.id)
            .single();

          if (!dealData) return;

          const otherParty = updatedDeal.buyer_id === user.id 
            ? dealData.seller 
            : dealData.buyer;

          // Build notification message
          let changeMessage = '';
          const timeChanged = oldDeal.meetup_time !== updatedDeal.meetup_time;
          const locationChanged = oldDeal.meetup_location_address !== updatedDeal.meetup_location_address;

          if (timeChanged && locationChanged) {
            changeMessage = `Time changed to ${format(new Date(updatedDeal.meetup_time), 'MMM d, h:mm a')} and location changed to ${updatedDeal.meetup_location_address}`;
          } else if (timeChanged) {
            changeMessage = `Time changed to ${format(new Date(updatedDeal.meetup_time), 'MMM d, h:mm a')}`;
          } else if (locationChanged) {
            changeMessage = `Location changed to ${updatedDeal.meetup_location_address}`;
          }

          if (changeMessage) {
            showNotification(
              `Deal Updated: ${dealData.listing.title}`,
              `${otherParty.name} updated the deal. ${changeMessage}`,
              `/chat`
            );
          }
        }
      )
      .subscribe();
  };

  const showNotification = (title: string, body: string, url?: string) => {
    if (Notification.permission !== 'granted') return;

    const notification = new Notification(title, {
      body,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: 'gem-notification',
      requireInteraction: true,
    });

    notification.onclick = () => {
      window.focus();
      if (url) {
        window.location.href = url;
      }
      notification.close();
    };

    // Auto-close after 10 seconds
    setTimeout(() => {
      notification.close();
    }, 10000);
  };

  return {
    requestNotificationPermission,
    showNotification,
  };
}
